import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  apiError,
  type CreateTemplateInput,
  type RoleRef,
  type ShiftTemplate,
} from './schedulingLib';

const BLANK: CreateTemplateInput = { name: '', startTime: '18:00', endTime: '23:00', breakMinutes: 0 };

/**
 * Reusable rota patterns — "Friday close", stamped onto dates from the
 * Roster tab (`ApplyTemplateDialog`) rather than typed by hand each week.
 * `branchId: null` (the "Every branch" option) is a tenant-wide pattern a
 * chain running one shape everywhere uses instead of one row per branch.
 */
export default function TemplatesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('scheduling.manage');
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [rows, setRows] = useState<ShiftTemplate[]>([]);
  const [roles, setRoles] = useState<RoleRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ShiftTemplate | null>(null);
  const [form, setForm] = useState<CreateTemplateInput & { isActive?: boolean }>(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<ShiftTemplate[]>('/scheduling/templates', { params: { branchId: branchId || undefined } })
      .then(({ data }) => setRows(data ?? []))
      .catch((e) => setError(apiError(e, t('scheduling.templates.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, t]);

  useEffect(load, [load]);
  useEffect(() => {
    if (!can('roles.view')) return;
    api
      .get('/roles', { params: { limit: 200 } })
      .then(({ data }) => setRoles((data?.data ?? data ?? []) as RoleRef[]))
      .catch(() => setRoles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, branchId: branchId || undefined });
    setFormOpen(true);
  };

  const openEdit = (tpl: ShiftTemplate) => {
    setEditing(tpl);
    setForm({
      name: tpl.name,
      roleId: tpl.roleId ?? undefined,
      startTime: tpl.startTime,
      endTime: tpl.endTime,
      breakMinutes: tpl.breakMinutes,
      isActive: tpl.isActive,
    });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/scheduling/templates/${editing.id}`, {
          name: form.name,
          roleId: form.roleId || undefined,
          startTime: form.startTime,
          endTime: form.endTime,
          breakMinutes: Number(form.breakMinutes) || 0,
          isActive: form.isActive,
        });
        toast.success(t('scheduling.templates.saved'));
      } else {
        await api.post('/scheduling/templates', {
          ...form,
          breakMinutes: Number(form.breakMinutes) || 0,
        });
        toast.success(t('scheduling.templates.created'));
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.templates.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const retire = async (tpl: ShiftTemplate) => {
    try {
      await api.patch(`/scheduling/templates/${tpl.id}`, { isActive: !tpl.isActive });
      load();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.templates.saveFailed')));
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          {branches.length > 0 && (
            <Field label={t('scheduling.roster.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('scheduling.templates.everyBranch')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {canManage && (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('scheduling.templates.newTemplate')}
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('scheduling.templates.empty')}
          hint={t('scheduling.templates.emptyHint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                {t('scheduling.templates.newTemplate')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('scheduling.templates.name')}</TH>
                <TH>{t('scheduling.templates.branch')}</TH>
                <TH>{t('scheduling.shift.role')}</TH>
                <TH>{t('scheduling.templates.times')}</TH>
                <TH>{t('common.status')}</TH>
                {canManage && <TH />}
              </TR>
            </THead>
            <tbody>
              {rows.map((tpl) => (
                <TR key={tpl.id}>
                  <TD className="font-medium">{tpl.name}</TD>
                  <TD>
                    {tpl.branchId
                      ? (branches.find((b) => b.id === tpl.branchId)?.name ?? tpl.branchId)
                      : t('scheduling.templates.everyBranch')}
                  </TD>
                  <TD>{tpl.role?.name ?? t('scheduling.shift.noRole')}</TD>
                  <TD className="tabular-nums">
                    {tpl.startTime}–{tpl.endTime}
                    {tpl.breakMinutes > 0 && (
                      <span className="text-ink-muted"> · {t('scheduling.templates.breakMin', { count: tpl.breakMinutes })}</span>
                    )}
                  </TD>
                  <TD>
                    <Badge tone={tpl.isActive ? 'success' : 'neutral'}>
                      {t(tpl.isActive ? 'scheduling.templates.active' : 'scheduling.templates.retired')}
                    </Badge>
                  </TD>
                  {canManage && (
                    <TD className="text-end">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(tpl)}>
                          {t('common.edit')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => void retire(tpl)}>
                          {t(tpl.isActive ? 'scheduling.templates.retire' : 'scheduling.templates.reactivate')}
                        </Button>
                      </div>
                    </TD>
                  )}
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'scheduling.templates.editTitle' : 'scheduling.templates.newTemplate')}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving || !form.name || !form.startTime || !form.endTime} onClick={() => void submit()}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label={t('scheduling.templates.name')}>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label={t('scheduling.shift.role')}>
            <Select
              value={form.roleId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value || undefined }))}
            >
              <option value="">{t('scheduling.shift.noRole')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('scheduling.shift.startTime')}>
              <Input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
              />
            </Field>
            <Field label={t('scheduling.shift.endTime')} hint={t('scheduling.shift.endTimeHint')}>
              <Input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
              />
            </Field>
          </div>
          <Field label={t('scheduling.shift.breakMinutes')}>
            <Input
              type="number"
              min={0}
              max={480}
              value={form.breakMinutes ?? 0}
              onChange={(e) => setForm((f) => ({ ...f, breakMinutes: Number(e.target.value) }))}
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
